import type { ReactElement } from 'react'

/**
 * Last-resort error boundary fallback. Rendered when something escapes
 * every other ErrorBoundary in the app. Uses `.ka-crash*` primitives from
 * `ka.css` so the surface still inherits theme tokens; the rules themselves
 * are minimal so the screen survives partial CSS load failures.
 *
 * Returns `ReactElement` (not `ReactNode`) because Sentry's
 * `<ErrorBoundary fallback>` prop accepts only a render-prop function or
 * `ReactElement`, never a broader `ReactNode`.
 */
interface CrashScreenProps {
  readonly error: unknown
  readonly resetError: () => void
}

export function CrashScreen({ error, resetError }: CrashScreenProps): ReactElement {
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred.'

  return (
    <div role="alert" className="ka-crash">
      <h1 className="ka-crash-title">Something went wrong</h1>
      <p className="ka-crash-body">{message}</p>
      <div className="ka-crash-actions">
        <button
          type="button"
          onClick={resetError}
          className="ka-crash-btn ka-crash-btn--secondary"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ka-crash-btn ka-crash-btn--primary"
        >
          Reload
        </button>
      </div>
    </div>
  )
}
